import { useEffect, useState } from 'react';
import { api } from '../api';
import { IconPin, IconShop } from '../components/icons';
import { Footer, TopBar } from '../components/TopBar';
import { mapsDirectionsUrl } from '../format';

interface PlaceDetail {
  public_id: string;
  name: string;
  category: string;
  address_1: string;
  address_2: string | null;
  city: string;
  state: string;
  postal_code: string;
  description: string | null;
  website_url: string | null;
  phone: string | null;
  credit_name: string | null;
  status: string;
}

export function PlaceListing({ publicId }: { publicId: string }) {
  const [p, setP] = useState<PlaceDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [flagMsg, setFlagMsg] = useState('');

  useEffect(() => {
    api
      .get<PlaceDetail>(`/shop-places/${publicId}`)
      .then(setP)
      .catch(() => setMissing(true));
  }, [publicId]);

  useEffect(() => {
    if (p) document.title = `${p.name}, ${p.city} ${p.state} · The Yard Sale Map`;
  }, [p]);

  async function flag() {
    setFlagMsg('Thanks — reported.');
    try {
      await api.postJson(`/shop-places/${publicId}/flag`, {});
    } catch {
      /* the message already shows regardless — this is a soft, best-effort report */
    }
  }

  if (missing) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>This place isn't available</h1>
          <p>It may have closed or been removed.</p>
          <a className="btn primary" href="/">
            Back to the map
          </a>
        </main>
        <Footer />
      </>
    );
  }
  if (!p) {
    return (
      <>
        <TopBar />
        <main className="page" aria-live="polite">
          <p>Loading…</p>
        </main>
      </>
    );
  }

  const full = `${p.address_1}, ${p.city}, ${p.state} ${p.postal_code}`;
  const safeWebsiteUrl = (() => {
    if (!p.website_url) return null;
    try {
      const u = new URL(p.website_url);
      return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
    } catch {
      return null;
    }
  })();

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        <span className="tag">
          <span
            className="ic"
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              width: '1em',
              height: '1em',
              verticalAlign: '-0.15em',
              marginRight: 4,
            }}
          >
            <IconShop />
          </span>
          {p.category || 'Local business'}
        </span>
        <h1>{p.name}</h1>
        <div className="facts">
          <span className="ic">
            <IconPin />
          </span>
          <div>
            <b>
              {p.address_1}
              {p.address_2 ? `, ${p.address_2}` : ''}
            </b>
            <span>
              {p.city}, {p.state} {p.postal_code}
            </span>
          </div>
        </div>
        <div className="actions">
          <a className="btn primary" href={mapsDirectionsUrl(full)} target="_blank" rel="noopener">
            Directions
          </a>
          {p.phone && <a href={`tel:${p.phone}`}>{p.phone}</a>}
          {safeWebsiteUrl && (
            <a href={safeWebsiteUrl} target="_blank" rel="noopener nofollow ugc">
              Website
            </a>
          )}
        </div>
        {p.description && (
          <div className="description">
            {p.description.split(/\n{2,}/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}
        {p.credit_name && <p className="hint">Added by {p.credit_name}</p>}
        <p className="hint" style={{ marginTop: 28 }}>
          {flagMsg || (
            <>
              Closed, or something wrong?{' '}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  void flag();
                }}
              >
                Let us know
              </a>
              .
            </>
          )}
        </p>
      </main>
      <Footer />
    </>
  );
}
